import React, { useEffect, useRef, useState } from 'react';
import { KeyboardArrowDown } from '@mui/icons-material';
import { Button } from '@mui/material';

import '../styles/components/DropdownMenuCheckSelect.less';

interface Option {
    id: number;
    value: string;
}

interface Props {
    options: Array<Option>;
    checkedList: Array<string>;
    setCheckedList: (options: Array<string>) => void;
    fieldText: string;
}

const DropdownMenuCheckSelect = ({
    options, // First option should be select all
    checkedList,
    setCheckedList,
    fieldText
}: Props) => {
    const [allChecked, setAllChecked] = useState(false);
    const [isOpen, setIsOpen] = useState(false);

    const dropdownContainerRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        if (checkedList?.length === options.length) {
            setAllChecked(true);
        }
    }, [checkedList, options]);

    const handleOptionClick = (option: Option) => {
        // Is the option checked?
        if (checkedList.some((o) => o === option.value)) {
            // Is the option the 'Select all' option?
            if (option.id === 0) {
                setAllChecked(false);
                setCheckedList([]);
            } else {
                // If an option is unchecked when 'Select all' is checked, both need to be unchecked.
                if (allChecked) {
                    setAllChecked(false);
                    let filteredList = checkedList.filter((o) => o !== 'Select all');
                    filteredList = filteredList.filter((o) => o !== option.value);
                    setCheckedList(filteredList);
                    // Uncheck a single option
                } else {
                    const filterList = checkedList.filter((o) => o !== option.value);
                    setCheckedList(filterList);
                }
            }
            // The option clicked was not selected, so check it!
        } else {
            // If the user is checking the 'Select all' option
            if (option.id === 0) {
                setAllChecked(true);
                setCheckedList(options.map((item) => item.value));
            } else {
                // If the user is checking an option
                let newCheckedList = [...checkedList, option.value].sort();
                // If the user is checking the last option, this should also check the select all option
                if (newCheckedList.length === options.length - 1) {
                    setAllChecked(true);
                    newCheckedList = options.map((item) => item.value);
                }
                setCheckedList(newCheckedList);
            }
        }
    };

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (dropdownContainerRef.current && !dropdownContainerRef.current.contains(event.target as Node)) {
                setIsOpen(false);
            }
        };

        window.addEventListener('click', handleClickOutside);

        return () => {
            window.removeEventListener('click', handleClickOutside);
        };
    }, []);

    return (
        <div ref={dropdownContainerRef} className="dropdown-checkbox-container">
            <Button
                variant="outlined"
                data-testid="select-location-button"
                endIcon={<KeyboardArrowDown />}
                onClick={() => setIsOpen(!isOpen)}
            >
                {fieldText}
            </Button>
            {isOpen && (
                <div className="dropdown-options" data-testid="dropdown-options">
                    {options.map((option) => (
                        <div
                            key={option.id}
                            onClick={() => handleOptionClick(option)}
                            className={`dropdown-option disable-select ${option.id === 0 ? 'all' : ''}`}
                            data-testid="dropdown-option"
                        >
                            <input
                                className="checkbox"
                                data-testid="option-checkbox"
                                type="checkbox"
                                checked={checkedList.some((o) => o === option.value)}
                                readOnly
                            />
                            <span data-testid="option-text">{option.value}</span>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
};

export default DropdownMenuCheckSelect;
